/**
 * Rules for starting a timeline range selection on top of an absence bar.
 *
 * FullCalendar refuses to begin a date selection when the mousedown lands on a
 * foreground event -- `isValidDateDownEl` rejects anything inside
 * `.fc-event:not(.fc-bg-event)`, so the document `mousemove` is never even
 * bound. To get the gesture back the app has to catch that mousedown itself and
 * hand the calendar a substitute one whose target is a plain lane element.
 *
 * The decisions live here rather than in the hook so they can be tested without
 * a DOM: the caller does the `closest()` lookups and passes the answers in.
 */

/** Movement, in pixels, before a press on a bar is read as a drag and not a click. */
export const DRAG_SELECT_THRESHOLD_PX = 4;

export type Point = { x: number; y: number };

export type EventDragStartInfo = {
  /** `MouseEvent.button` -- only the primary button selects. */
  button: number;
  /** Ctrl-click is a right-click on macOS; leave it to the browser. */
  ctrlKey: boolean;
  /** The press is inside a lane of the timeline body, not a header or the resource column. */
  inTimelineLane: boolean;
  /**
   * Month view only. In day view `selectAllow` rejects any range overlapping an
   * absence, so the gesture would be vetoed anyway, and day-view bars carry the
   * resize handles that must keep working.
   */
  isMonthView: boolean;
  /** The press is inside a real absence bar (`.fc-event`, background events excluded). */
  onEvent: boolean;
  /** The press is on a resize handle -- that gesture belongs to FullCalendar. */
  onResizer: boolean;
  /** The press is on a preview bar painting the pending selection, not an absence. */
  onTransient: boolean;
};

/**
 * Whether this mousedown is the one case FullCalendar gets wrong: a press on an
 * absence bar that should be allowed to grow into a range selection.
 *
 * Everything else is left alone, so no other press on the timeline changes
 * behaviour.
 */
export function shouldCaptureEventDragStart({
  button,
  ctrlKey,
  inTimelineLane,
  isMonthView,
  onEvent,
  onResizer,
  onTransient,
}: EventDragStartInfo): boolean {
  if (button !== 0 || ctrlKey) return false;
  if (!isMonthView) return false;
  if (!onEvent || !inTimelineLane) return false;
  return !onResizer && !onTransient;
}

/**
 * Whether the pointer has travelled far enough for the press to be a drag.
 *
 * A press that never passes this is left to run its course as an ordinary
 * click, which is what keeps opening the details pane working.
 */
export function hasPassedDragThreshold(
  origin: Point,
  point: Point,
  threshold: number = DRAG_SELECT_THRESHOLD_PX,
): boolean {
  const dx = point.x - origin.x;
  const dy = point.y - origin.y;
  return dx * dx + dy * dy >= threshold * threshold;
}
